import { endOfMonth, format, isValid, parse } from "date-fns";
// @ts-expect-error TS(2305)
import Highcharts, { Gradient } from "highcharts";
import { uniq } from "lodash";

import { DateFormat } from "constants/date";
import { CustomDateRangeFilterId, Filter, IDateRangeFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { NavigationItems } from "constants/reports/navigationItems";
import { ReportType } from "constants/reports/report-type";
import { ensureArray } from "helpers/data";
import { deepMerge } from "helpers/object";
import { getFiltersByReportType } from "helpers/reports";
import { navigate } from "helpers/routing";
import { formatTimeFromHours } from "helpers/time";
import { IKeyValue, stringifyQueryParams } from "helpers/url";
import { ISideNavigationItem } from "interfaces/sideNavigationItem";
import { AppStateProvider } from "services/app-state-provider";
import { Plan } from "store/entities/subscriptions";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import {
  IChartSeries,
  IRanking,
  getCurrentView,
  getFilters,
  hasReportComparisonDisabled,
  hasReportFiltersComparison,
} from "store/views/reports";
import { getReportsViewQueryParams } from "store/views/reports/url-query-params";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { IChartLegend } from "./components";

export function getExportFilename(
  name: string,
  dateRange: IDateRangeFilter,
  distribution: ReportDistribution = ReportDistribution.Day
): string {
  const date =
    dateRange.id && dateRange.id !== CustomDateRangeFilterId.CustomPeriod
      ? [dateRange.id]
      : [
          // @ts-expect-error TS(2345)
          typeof dateRange.from === "string" ? dateRange.from : format(dateRange.from, DateFormat.HtmlInputDate),
          // @ts-expect-error TS(2345)
          typeof dateRange.to === "string" ? dateRange.to : format(dateRange.to, DateFormat.HtmlInputDate),
        ];

  return ["livechatinc", name, ...date, distribution === ReportDistribution.Hour ? "24h" : null]
    .filter(Boolean)
    .join("_");
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getExportDataByHeatmapOptions(heatmapOptions: any): (string | number)[][] {
  const categoriesX = heatmapOptions.xAxis.categories;
  const categoriesY = heatmapOptions.yAxis.categories;

  return [
    ["date", "time", "value"],
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...heatmapOptions.series[0].data.map((element: any) => {
      const day = format(categoriesX[element.x].value, DateFormat.HtmlInputDate);
      const hour = categoriesY[element.y];
      return [day, hour, element.value];
    }),
  ];
}

export function getExportDataByChartOptions(
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartOptions: any,
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  mapper = (series: any) => series
): (string | number)[][] {
  if (chartOptions.chart.type === "heatmap") {
    return getExportDataByHeatmapOptions(chartOptions);
  }
  const rows = [];

  const distribution = chartOptions.xAxis.distribution || ReportDistribution.Day;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const categories = (chartOptions.xAxis.categories || []).map((category: any) =>
    distribution !== ReportDistribution.Hour ? category.value : category.label
  );

  const series = [...(chartOptions.series || [])]
    .sort((a, b) => {
      const orderA = a.csvColumnOrder || 0;
      const orderB = b.csvColumnOrder || 0;

      return orderA - orderB;
    })
    .map(mapper);

  if (!series.length) {
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type '(string | n... Remove this comment to see the full error message
    return null;
  }

  rows.push([
    distribution !== ReportDistribution.Hour ? "date" : "time",
    ...series.map(s => (s.csvColumnName || s.name || "").toLowerCase()),
  ]);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.push(...categories.map((label: any, index: any) => [label, ...series.map(s => s.data[index] || 0)]));

  return rows;
}

export const getNavigationResolver =
  // prettier-ignore
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  (pathname = "/archives", queryParamsParser = (params: any) => params): any =>
    (
      distribution: ReportDistribution // FIXME: Fix this the next time the file is edited.
    ) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((function navigationResolver(this: any): any {
        const value = Array.isArray(this.category.value)
          ? this.category.value[this.series.index % this.category.value.length] || this.category.value[0]
          : this.category.value;

        const seriesFilters = this.series.userOptions.filters;

        if (isValid(new Date(value))) {
          const state = AppStateProvider.getState();
          const filters = getFilters(state);
          const page = getCurrentView(state);
          const availableFilters = getFiltersByReportType(page);
          const reportsViewQueryParams = getReportsViewQueryParams(state);

          reportsViewQueryParams.date_from = value;
          reportsViewQueryParams.date_to = value;

          if (distribution === ReportDistribution.Month) {
            reportsViewQueryParams.date_to = format(endOfMonth(parse(value)), DateFormat.HtmlInputDate);
          }

          // AA-5751: fix for "No tagged" filters
          if (filters.tag && availableFilters.includes(Filter.Tag)) {
            reportsViewQueryParams.tag = ensureArray(filters.tag);
          }

          if (seriesFilters) {
            if (seriesFilters.group) {
              reportsViewQueryParams.group = seriesFilters.group;
            }
          }

          // @ts-expect-error TS(2554): Expected 1 arguments, but got 2.
          const parsedQueryParams = queryParamsParser(reportsViewQueryParams, this);

          navigate(`${pathname}?${stringifyQueryParams(parsedQueryParams)}`);
        }
      }));

export function defaultLegendQueryParamsParser(params: IKeyValue): IKeyValue {
  const queryParams = { ...params };

  delete queryParams.group_by;

  return queryParams;
}

export const getLegendNavigationResolver =
  // prettier-ignore
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (pathname = "/archives", queryParamsParser = defaultLegendQueryParamsParser): any =>
  (
    { urlParams, filters }: IChartLegend
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any => {
    const state = AppStateProvider.getState();

    const filtersQueryParams = getReportsViewQueryParams(state);

    const hasComparison = !hasReportComparisonDisabled(state) && hasReportFiltersComparison(state);

    if (hasComparison) {
      // @ts-expect-error TS(18048)
      if (filters.dateFrom && filters.dateTo && filtersQueryParams.date_from && filtersQueryParams.date_to) {
        // @ts-expect-error TS(18048)
        filtersQueryParams.date_from = filters.dateFrom;
        // @ts-expect-error TS(18048)
        filtersQueryParams.date_to = filters.dateTo;
      }

      // @ts-expect-error TS(18048)
      if (filters.group && filtersQueryParams.group) {
        // @ts-expect-error TS(18048)
        filtersQueryParams.group = filters.group;
      }

      // @ts-expect-error TS(18048)
      if (filters.agent && filtersQueryParams.agent) {
        // @ts-expect-error TS(18048)
        filtersQueryParams.agent = filters.agent;
      }

      delete filtersQueryParams.compare_date_from;
      delete filtersQueryParams.compare_date_to;
      delete filtersQueryParams.compare_group;
      delete filtersQueryParams.compare_agent;
    }

    const params = {
      ...filtersQueryParams,
      ...(urlParams || {}),
    };

    return `${pathname}?${stringifyQueryParams(queryParamsParser(params))}`;
  };

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getWeekDayLabelsFromPeriod(value: any): string {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return days[new Date(value).getDay()];
}

export function getFormattedChartLabel(value: string, distribution: ReportDistribution): string {
  if (distribution === ReportDistribution.Hour) {
    return formatTimeFromHours(value);
  }
  if (distribution === ReportDistribution.Month) {
    return format(value, DateFormat.ChartMonthlyLabel);
  }
  return format(value, DateFormat.ChartDailyLabel);
}

export function getDistributionChartLabel(values: string | string[], distribution: ReportDistribution): string {
  if (!Array.isArray(values)) {
    return values;
  }
  return values.map(value => getFormattedChartLabel(value, distribution)).join("<br>");
}

function defaultLabelFormatter(value: string | string[]): string {
  if (Array.isArray(value)) {
    return value.join("<br>");
  }
  return value;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getDistributionOptions(distribution: ReportDistribution, xLabels: (string[] | string)[]): any {
  const navigationOptions = distribution === ReportDistribution.Day && {
    plotOptions: {
      series: {
        cursor: undefined,
        point: undefined,
      },
    },
  };
  return {
    xAxis: {
      categories: xLabels.map(values => ({ label: getDistributionChartLabel(values, distribution), values })),
    },
    ...navigationOptions,
  };
}

export // FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getChartOptions(
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  series: any[],
  xLabels: (string | string[])[],
  distribution: ReportDistribution = ReportDistribution.Day,
  navigationResolver = getNavigationResolver(),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...options: any[]
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  const chartData = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
    },
    xAxis: {
      categories: xLabels.map(value => ({ label: defaultLabelFormatter(value), value })),
      distribution,
      labels: {
        format: "{value.label}",
      },
    },
    yAxis: {
      max: [].concat(...series.map(s => s.data)).reduce((sum, curr) => sum + curr, 0) === 0 ? 1 : null,
    },
    series,
  };

  const navigationOptions = distribution !== ReportDistribution.Hour &&
    navigationResolver && {
      plotOptions: {
        series: {
          cursor: "pointer",
          point: {
            events: {
              click: navigationResolver(distribution),
            },
          },
        },
      },
    };

  const distributionOptions = distribution && getDistributionOptions(distribution, xLabels);

  const forceReportType = xLabels.length > 21 && {
    chart: {
      type: "area",
    },
    xAxis: {
      tickInterval: xLabels.length > 35 ? 7 : 4,
    },
  };

  return deepMerge(true, {}, chartData, navigationOptions, distributionOptions, forceReportType, ...options);
}

export const getNavigationItems = (
  hasReportExportOption: boolean,
  hasAuditLog: boolean,
  hasPerformanceReport: boolean,
  isAdmin: boolean
): ISideNavigationItem[] =>
  NavigationItems(hasReportExportOption, hasAuditLog).filter(item => {
    if (!hasPerformanceReport && item.id === ReportType.HandlingTimeByStatus) {
      return false;
    }
    if (!isAdmin && item.id === ReportType.AuditLog) {
      return false;
    }
    return true;
  });

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSeriesToGradient(series: any): any {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line import/no-named-as-default-member
  const color = new Highcharts.Color(series.color) as Gradient;

  return {
    ...series,
    ...(series.color && {
      fillColor: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, color.setOpacity(series.fillOpacity || 0.2).get("hex")],
          [1, color.setOpacity(0).get("hex")],
        ],
      },
    }),
  };
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSeriesColors(series: any[]): (string | null)[] {
  const existColors = {};

  const colorsCount = uniq(series.map(({ color }) => color)).length;
  const compareColors = series.length / colorsCount;
  const brightenStep = 0.3 / compareColors;

  const getEffectiveColor = (color: string): string => {
    if (color && color.startsWith("var")) {
      return getComputedStyle(document.documentElement).getPropertyValue(color.slice(4, -1)).trim();
    }
    return color;
  };

  const brightenColor = (color: string): string => {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const count = existColors[color] || 0;
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    existColors[color] = count + 1;
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line import/no-named-as-default-member
    return (new Highcharts.Color(color) as Gradient).brighten(count * brightenStep).get("hex");
  };
  return series.map(({ color }) => {
    const effectiveColor = getEffectiveColor(color);
    return effectiveColor ? brightenColor(effectiveColor) : null;
  });
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSeriesColorToSwatches(series: any, index: any, colors: (string | null)[]): any {
  if (!series.color) {
    return series;
  }

  return {
    ...series,
    color: colors[index],
  };
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapChartSeriesColors(seriesList: IChartSeries[], activeSeries: null | string): any {
  return seriesList
    .map((series, index, allSeries) => mapSeriesColorToSwatches(series, index, getSeriesColors(allSeries)))
    .map(series =>
      activeSeries !== null && series.color
        ? {
            ...series,
            // FIXME: Fix this the next time the file is edited.
            // eslint-disable-next-line import/no-named-as-default-member
            color: (new Highcharts.Color(series.color) as Gradient)
              .setOpacity(series.color !== activeSeries ? 0.1 : 1)
              .get("hex"),
            fillOpacity: series.color !== activeSeries ? 0.05 : 0.2,
          }
        : series
    )
    .map(mapSeriesToGradient);
}

export function mapChartLegendColors(legends: IChartLegend[]): IChartLegend[] {
  return legends.map((legend, index) => mapSeriesColorToSwatches(legend, index, getSeriesColors(legends)));
}

export function sortRanking(a: IRanking, b: IRanking): number {
  const greatDiff = b.ratedGreatPercentage - a.ratedGreatPercentage;
  if (greatDiff !== 0) {
    return greatDiff;
  }

  const badDiff = b.ratedBadPercentage - a.ratedBadPercentage;
  if (badDiff !== 0) {
    return -badDiff;
  }

  return 0;
}

export function getRetentionPeriod(currentPlan: Plan): number {
  if (currentPlan === Plan.Business) {
    return 30;
  }
  const auditLogReleaseDate = new Date("2024-07-17");
  const today = new Date();
  const monthsDiff =
    (today.getFullYear() - auditLogReleaseDate.getFullYear()) * 12 +
    (today.getMonth() - auditLogReleaseDate.getMonth());

  if (monthsDiff == 0) {
    return 30;
  }
  if (monthsDiff >= 1 && monthsDiff < 12) {
    return 30 * monthsDiff;
  }

  return 365;
}
